import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { AssetValueContent } from './AssetValuePage';
import VehicleDocsDrawer from '../components/VehicleDocsDrawer';
import FleetSwapsTab from '../components/FleetSwapsTab';
import FleetInsuranceModal from '../components/FleetInsuranceModal';
import {
  Truck, Plus, Wrench, X, Save, Search,
  ExternalLink, AlertTriangle, ChevronDown, ChevronUp,
  RefreshCw, CheckCircle, XCircle, Loader, FileText, ShieldCheck,
  Archive, RotateCcw, Camera,
} from 'lucide-react';

const SNSW_REGO_URL   = 'https://check-registration.service.nsw.gov.au/frc';
const REGO_CHECK_URL  = '/.netlify/functions/rego-check';

const OWNER_OPTIONS = [
  { value: 'PHB',                  short: 'PHB',      label: 'PH Bouveng Pty Ltd (PHB)' },
  { value: 'HPL',                  short: 'HPL',      label: 'Hollypaul Leasing Pty Ltd (HPL)' },
  { value: 'SG Fleet',             short: 'SG Fleet', label: 'SG Fleet' },
  { value: 'Owner Driver',         short: 'O/D',      label: 'Owner Driver' },
  { value: 'PH Bouveng Pty Ltd',   short: 'PHB',      label: 'PH Bouveng Pty Ltd (PHB)' },
  { value: 'Hollypaul Leasing Pty Ltd', short: 'HPL', label: 'Hollypaul Leasing Pty Ltd (HPL)' },
];

const OWNER_COLORS = {
  'PH Bouveng Pty Ltd':        { bg: '#2563eb',           color: '#fff' },
  'PHB':                       { bg: '#2563eb',           color: '#fff' },
  'Hollypaul Leasing Pty Ltd': { bg: 'var(--hp-gold)',    color: '#000' },
  'HPL':                       { bg: 'var(--hp-gold)',    color: '#000' },
  'SG Fleet':                  { bg: '#7c3aed',           color: '#fff' },
  'Owner Driver':              { bg: 'var(--hp-green)',   color: '#000' },
};

function OwnerBadge({ owner }) {
  if (!owner) return <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>—</span>;
  const opt   = OWNER_OPTIONS.find(o => o.value === owner);
  const style = OWNER_COLORS[owner] || { bg: 'var(--hp-surface)', color: 'var(--hp-dim)' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem',
      fontWeight: 700, fontFamily: 'var(--font-mono)', textTransform: 'uppercase',
      letterSpacing: '0.05em', background: style.bg, color: style.color,
    }}>
      {opt?.short || owner}
    </span>
  );
}

// Fleet now runs on Supabase (fleet-sb); was the appsscript proxy.
const FLEET_URL = '/.netlify/functions/fleet-sb';

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.floor((d - new Date()) / 86400000);
}

function checkRego(rego, addNotification) {
  if (navigator.clipboard) navigator.clipboard.writeText(rego).catch(() => {});
  window.open(SNSW_REGO_URL, '_blank');
  if (addNotification) addNotification(`${rego} copied to clipboard — paste into the rego check form`, 'info');
}

// Coloured pill badge for rego status
function RegoStatusPill({ days, hasDate }) {
  const pill = (bg, color, text) => (
    <span style={{
      display: 'inline-block', padding: '3px 11px', borderRadius: 20,
      fontSize: '0.7rem', fontWeight: 700, fontFamily: 'var(--font-mono)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      background: bg, color,
    }}>{text}</span>
  );
  if (!hasDate || days === null)
    return pill('var(--hp-surface)', 'var(--hp-muted)', 'No Date');
  if (days < 0)
    return pill('var(--hp-red)', '#fff', `Expired ${Math.abs(days)}d`);
  if (days === 0)
    return pill('var(--hp-red)', '#fff', 'Due Today');
  if (days < 14)
    return pill('var(--hp-amber)', '#000', `${days}d left`);
  if (days < 60)
    return pill('var(--hp-gold)', '#000', `${days}d left`);
  return pill('rgba(0,0,0,0)', 'var(--hp-green)', `✓ ${days}d`);
}

function rowAccent(days, hasDate) {
  if (!hasDate || days === null) return 'var(--hp-border)';
  if (days < 0)  return 'var(--hp-red)';
  if (days < 14) return 'var(--hp-amber)';
  if (days < 60) return 'var(--hp-gold)';
  return 'var(--hp-green)';
}

// When this plate's registration was last verified online. The check is paid quota,
// and it can be spent from EITHER page — Compliance ▸ Check rego records it against
// the driver, and fleet-sb reads it back here — so say when it was done rather than
// let someone buy the same answer twice.
function LastVerified({ at }) {
  if (!at) return null;
  const d = new Date(at);
  if (isNaN(d)) return null;
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  return (
    <span style={{ fontSize: '0.66rem', color: 'var(--hp-muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}
      title={`Registration verified online on ${d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}`}>
      <ShieldCheck size={9} /> verified {days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`}
    </span>
  );
}

// Where an insurance date came from. A van's cover can be evidenced on either page —
// the company policy is uploaded on Fleet, an owner-driver's certificate of currency
// arrives in the driver's portal — and the two now carry each other's date
// (netlify/functions/_shared/fleet-insurance.js). Saying which one this is keeps a
// date that appeared from the other page attributable.
function FromCertificate({ at }) {
  if (!at) return null;
  const d = new Date(at);
  return (
    <span style={{ fontSize: '0.66rem', color: 'var(--hp-muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}
      title={isNaN(d) ? "From the driver's certificate of currency on Compliance"
        : `From the driver's certificate of currency, uploaded ${d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}`}>
      <FileText size={9} /> driver's certificate
    </span>
  );
}

function CompliancePill({ label, date, verifiedAt, certifiedAt }) {
  if (!date) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ fontSize: '0.68rem', color: 'var(--hp-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
        <span style={{ fontSize: '0.8rem', color: 'var(--hp-muted)' }}>—</span>
      </div>
    );
  }
  const days = daysUntil(date);
  const color = days === null ? 'var(--hp-muted)' : days < 0 ? 'var(--hp-red)' : days < 30 ? 'var(--hp-amber)' : days < 60 ? 'var(--hp-gold)' : 'var(--hp-dim)';
  const formatted = new Date(date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{ fontSize: '0.68rem', color: 'var(--hp-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
      <span style={{ fontSize: '0.8rem', color, fontWeight: days !== null && days < 60 ? 600 : 400 }}>
        {days !== null && days < 0 ? '⚠ ' : ''}{formatted}
        {days !== null && days >= 0 && days < 60 && <span style={{ fontSize: '0.7rem', color: 'var(--hp-muted)', marginLeft: '4px' }}>({days}d)</span>}
      </span>
      <LastVerified at={verifiedAt} />
      <FromCertificate at={certifiedAt} />
    </div>
  );
}

// ── Alert Panel ───────────────────────────────────────────────────────────────

function AlertPanel({ vehicles, addNotification }) {
  const [open, setOpen] = useState(true);

  const alerts = useMemo(() => {
    return vehicles.flatMap(v => {
      const items = [];
      const rd = daysUntil(v.rego_exp);
      const id = daysUntil(v.insurance_exp);
      const sd = daysUntil(v.service_due);
      if (rd !== null && rd < 60)
        items.push({ rego: v.rego, type: 'Rego',      days: rd, date: v.rego_exp,      isRego: true });
      if (id !== null && id < 30)
        items.push({ rego: v.rego, type: 'Insurance', days: id, date: v.insurance_exp, isRego: false });
      if (sd !== null && sd < 14)
        items.push({ rego: v.rego, type: 'Service',   days: sd, date: v.service_due,   isRego: false });
      return items;
    }).sort((a, b) => a.days - b.days);
  }, [vehicles]);

  if (alerts.length === 0) return null;

  const expired = alerts.filter(a => a.days < 0).length;
  const accent  = expired > 0 ? 'var(--hp-red)' : 'var(--hp-amber)';

  return (
    <div style={{
      background: 'var(--hp-surface)',
      border: `1px solid ${accent}44`,
      borderRadius: 10, marginBottom: 20, overflow: 'hidden',
    }}>
      {/* Header row */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '13px 18px', background: 'none', border: 'none',
          cursor: 'pointer', color: 'inherit',
          borderBottom: open ? '1px solid var(--hp-border)22' : 'none',
        }}
      >
        <AlertTriangle size={15} color={accent} />
        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {alerts.length} Compliance Alert{alerts.length !== 1 ? 's' : ''}
        </span>
        {expired > 0 && (
          <span style={{
            background: 'var(--hp-red)', color: '#fff', borderRadius: 20,
            fontSize: '0.68rem', fontWeight: 800, padding: '2px 9px',
            letterSpacing: '0.05em', fontFamily: 'var(--font-mono)',
          }}>
            {expired} EXPIRED
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--hp-muted)', display: 'flex' }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {alerts.map((a, i) => {
            const accent = rowAccent(a.days, true);
            const dateStr = new Date(a.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
            return (
              <div
                key={i}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '10px 18px',
                  borderBottom: i < alerts.length - 1 ? '1px solid var(--hp-border)22' : 'none',
                  boxShadow: `inset 4px 0 0 ${accent}`,
                }}
              >
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '0.85rem', color: 'var(--hp-gold)', minWidth: 72 }}>
                  {a.rego}
                </span>
                <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', minWidth: 70 }}>{a.type}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--hp-muted)', flex: 1 }}>{dateStr}</span>
                <RegoStatusPill days={a.days} hasDate />
                {a.isRego && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => checkRego(a.rego, addNotification)}
                    style={{ marginLeft: 4 }}
                  >
                    <ExternalLink size={11} /> Check
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Rego Check Modal ──────────────────────────────────────────────────────────

function RegoCheckModal({ vehicles, personnel, onClose, addNotification }) {
  const [tab, setTab] = useState('all');

  const getDriver = login =>
    login ? personnel.find(p => p.loginName?.toUpperCase() === login?.toUpperCase()) : null;

  const allRows = useMemo(() => (
    [...vehicles]
      .filter(v => v.rego)
      .map(v => ({ ...v, regoDays: daysUntil(v.rego_exp) }))
      .sort((a, b) => {
        if (a.regoDays === null && b.regoDays === null) return 0;
        if (a.regoDays === null) return 1;
        if (b.regoDays === null) return -1;
        return a.regoDays - b.regoDays;
      })
  ), [vehicles]);

  const expiringRows = allRows.filter(v => v.regoDays !== null && v.regoDays < 60);
  const nodateRows   = allRows.filter(v => !v.rego_exp);
  const displayed    = tab === 'expiring' ? expiringRows : tab === 'nodate' ? nodateRows : allRows;

  const TABS = [
    { key: 'all',      label: 'All',       count: allRows.length },
    { key: 'expiring', label: 'Expiring',  count: expiringRows.length },
    { key: 'nodate',   label: 'No Date',   count: nodateRows.length },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1000,
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
      paddingTop: 50, overflowY: 'auto',
    }}>
      <div style={{
        width: 760, maxHeight: '84vh', background: 'var(--hp-bg)',
        borderRadius: 14, border: '1px solid var(--hp-border)',
        display: 'flex', flexDirection: 'column', margin: '0 16px',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      }}>

        {/* ── Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: '22px 26px 18px', borderBottom: '1px solid var(--hp-border)', flexShrink: 0,
        }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.63rem', textTransform: 'uppercase',
              letterSpacing: '0.14em', color: 'var(--hp-gold)', marginBottom: 5,
            }}>
              Fleet · Service NSW
            </div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.2rem', fontWeight: 700 }}>
              Registration Check
            </h3>
            <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--hp-muted)' }}>
              Clicking <strong style={{ color: 'var(--hp-text)' }}>Check</strong> copies the plate to clipboard and opens the Service NSW rego check form — just paste and go
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, marginLeft: 20 }}>
            <a
              href={SNSW_REGO_URL} target="_blank" rel="noopener noreferrer"
              className="btn btn-secondary btn-sm"
              style={{ textDecoration: 'none' }}
            >
              <ExternalLink size={12} /> Open Service NSW
            </a>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        {/* ── Tabs */}
        <div style={{
          display: 'flex', borderBottom: '1px solid var(--hp-border)',
          padding: '0 26px', flexShrink: 0, gap: 2,
        }}>
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  padding: '12px 14px', border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: '0.82rem', fontWeight: active ? 700 : 400,
                  color: active ? 'var(--hp-text)' : 'var(--hp-muted)',
                  borderBottom: active ? '2px solid var(--hp-gold)' : '2px solid transparent',
                  marginBottom: -1, display: 'flex', alignItems: 'center', gap: 7,
                }}
              >
                {t.label}
                <span style={{
                  padding: '1px 7px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 800,
                  background: active ? 'var(--hp-gold)' : 'var(--hp-surface)',
                  color: active ? '#000' : 'var(--hp-muted)',
                }}>
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── Table */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: 'var(--hp-bg)', zIndex: 1 }}>
              <tr style={{ borderBottom: '1px solid var(--hp-border)' }}>
                {['', 'Registration', 'Vehicle & Driver', 'Expiry Date', 'Status', ''].map((h, i) => (
                  <th key={i} style={{
                    textAlign: 'left', padding: i === 0 ? 0 : '11px 16px',
                    color: 'var(--hp-muted)', fontWeight: 600, fontSize: '0.68rem',
                    textTransform: 'uppercase', letterSpacing: '0.09em',
                    width: i === 0 ? 4 : 'auto',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map(v => {
                const driver  = getDriver(v.login);
                const accent  = rowAccent(v.regoDays, !!v.rego_exp);
                const expDate = v.rego_exp
                  ? new Date(v.rego_exp).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
                  : null;
                return (
                  <tr
                    key={v.rego}
                    style={{
                      borderBottom: '1px solid var(--hp-border)22',
                      boxShadow: `inset 4px 0 0 ${accent}`,
                    }}
                  >
                    <td style={{ width: 4, padding: 0 }} />
                    <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontWeight: 800,
                        fontSize: '0.92rem', color: 'var(--hp-gold)',
                      }}>{v.rego}</span>
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ fontSize: '0.83rem', fontWeight: 500, color: 'var(--hp-text)' }}>
                        {v.make || v.model
                          ? `${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim()
                          : <span style={{ color: 'var(--hp-muted)', fontStyle: 'italic' }}>No details</span>
                        }
                      </div>
                      {(driver || v.login) && (
                        <div style={{ fontSize: '0.74rem', color: 'var(--hp-muted)', marginTop: 3 }}>
                          {driver ? `${driver.name} · ${driver.loginName}` : v.login}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '13px 16px', fontSize: '0.83rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>
                      <div>{expDate || <span style={{ color: 'var(--hp-muted)' }}>—</span>}</div>
                      <LastVerified at={v.regoCheckedAt} />
                    </td>
                    <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
                      <RegoStatusPill days={v.regoDays} hasDate={!!v.rego_exp} />
                    </td>
                    <td style={{ padding: '13px 16px', textAlign: 'right' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => checkRego(v.rego, addNotification)}
                      >
                        <ExternalLink size={11} /> Check
                      </button>
                    </td>
                  </tr>
                );
              })}
              {displayed.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--hp-muted)', fontSize: '0.85rem' }}>
                    No vehicles in this category
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Footer */}
        <div style={{
          padding: '12px 26px', borderTop: '1px solid var(--hp-border)', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 16, fontSize: '0.74rem', color: 'var(--hp-muted)',
        }}>
          {[
            { color: 'var(--hp-red)',    label: 'Expired' },
            { color: 'var(--hp-amber)',  label: '< 14 days' },
            { color: 'var(--hp-gold)',   label: '< 60 days' },
            { color: 'var(--hp-green)',  label: 'OK' },
            { color: 'var(--hp-border)', label: 'No date' },
          ].map(({ color, label }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0, display: 'inline-block' }} />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Sync Progress Modal ───────────────────────────────────────────────────────

function SyncProgressModal({ state, onClose }) {
  const { running, total, done, results, currentRego } = state;
  const pct      = total > 0 ? Math.round((done / total) * 100) : 0;
  const updated  = results.filter(r => r.status === 'updated').length;
  const notFound = results.filter(r => r.status === 'not_found').length;
  const errors   = results.filter(r => r.status === 'error').length;

  const listRef = useRef(null);
  // Auto-scroll results list as items come in
  useMemo(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [results.length]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.78)', zIndex:1000, display:'flex', justifyContent:'center', alignItems:'center' }}>
      <div style={{ width:560, maxHeight:'80vh', background:'var(--hp-bg)', borderRadius:14, border:'1px solid var(--hp-border)', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,0.5)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid var(--hp-border)', flexShrink:0 }}>
          <div style={{ fontFamily:'var(--font-mono)', fontSize:'.63rem', textTransform:'uppercase', letterSpacing:'.14em', color:'var(--hp-gold)', marginBottom:5 }}>Fleet · Rego Sync</div>
          <h3 style={{ margin:'0 0 4px', fontSize:'1.1rem', fontWeight:700 }}>
            {running ? 'Syncing Registration Dates…' : 'Sync Complete'}
          </h3>
          {!running && (
            <p style={{ margin:0, fontSize:'0.8rem', color:'var(--hp-muted)' }}>
              <span style={{ color:'var(--hp-green)', fontWeight:600 }}>{updated} updated</span>
              {notFound > 0 && <span style={{ color:'var(--hp-muted)' }}> · {notFound} not found</span>}
              {errors   > 0 && <span style={{ color:'var(--hp-red)',   fontWeight:600 }}> · {errors} errors</span>}
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div style={{ padding:'14px 24px', borderBottom:'1px solid var(--hp-border)', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:'0.78rem', color:'var(--hp-muted)' }}>
            <span>{running ? `Checking ${done + 1} of ${total}${currentRego ? ` — ${currentRego}` : ''}` : `${done} of ${total} vehicles checked`}</span>
            <span style={{ fontWeight:600, color:'var(--hp-text)' }}>{pct}%</span>
          </div>
          <div style={{ height:6, background:'var(--hp-surface)', borderRadius:3, overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, background: running ? 'var(--hp-gold)' : 'var(--hp-green)', borderRadius:3, transition:'width 0.3s ease' }} />
          </div>
        </div>

        {/* Results list */}
        <div ref={listRef} style={{ overflowY:'auto', flex:1, padding:'6px 0' }}>
          {results.map((r, i) => {
            const vehicleLabel = [r.year, r.make, r.model].filter(Boolean).join(' ');
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'7px 24px', borderBottom:'1px solid var(--hp-border)11' }}>
                {r.status === 'updated'   && <CheckCircle size={14} color="var(--hp-green)" />}
                {r.status === 'not_found' && <XCircle     size={14} color="var(--hp-muted)" />}
                {r.status === 'error'     && <XCircle     size={14} color="var(--hp-red)"   />}
                <span style={{ fontFamily:'var(--font-mono)', fontWeight:800, color:'var(--hp-gold)', minWidth:72, fontSize:'0.85rem' }}>{r.rego}</span>
                <span style={{ fontSize:'0.8rem', color:'var(--hp-dim)', flex:1 }}>
                  {r.status === 'updated'   ? (vehicleLabel || 'Details synced')
                   : r.status === 'not_found' ? 'Not found in registry'
                   : `Error: ${r.error || 'unknown'}`}
                </span>
              </div>
            );
          })}
          {running && currentRego && (
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'7px 24px', opacity:0.5 }}>
              <Loader size={14} color="var(--hp-gold)" style={{ animation:'spin 1s linear infinite' }} />
              <span style={{ fontFamily:'var(--font-mono)', fontWeight:800, color:'var(--hp-gold)', minWidth:72, fontSize:'0.85rem' }}>{currentRego}</span>
              <span style={{ fontSize:'0.8rem', color:'var(--hp-muted)' }}>Checking…</span>
            </div>
          )}
        </div>

        {/* Footer */}
        {!running && (
          <div style={{ padding:'16px 24px', borderTop:'1px solid var(--hp-border)', flexShrink:0 }}>
            <button className="btn btn-primary" style={{ width:'100%' }} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Form helpers ──────────────────────────────────────────────────────────────

const labelStyle = {
  fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5,
};
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)' };

function SectionTitle({ n, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, marginTop: n > 1 ? 20 : 0 }}>
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--hp-gold)', color: '#000', fontSize: '.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--hp-gold)', fontWeight: 700 }}>{title}</span>
    </div>
  );
}

// Plain-English names for the dry-run's `table.column` keys, so the impact panel
// reads as work records rather than as schema.
const REGO_REF_LABELS = {
  'fleet.rego': 'Vehicle record',
  'vehicle_assignments.rego': 'Custody / swap history',
  'vehicle_service.rego': 'Service history',
  'vehicle_profiles.rego': 'Service intervals',
  'vehicle_documents.rego': 'Vehicle documents',
  'vehicle_inspections.rego': 'Morning van checks',
  'vehicle_issues.rego': 'Vehicle issues',
  'vehicle_claims.rego': 'Insurance claims',
  'vehicle_valuations.rego': 'Valuations',
  'traffic_fines.rego': 'Fines',
  'odometer_log.rego': 'Odometer readings',
  'drivers.rego': 'Driver record (rego)',
  'drivers.vehicle': 'Driver record (vehicle)',
  'active_shifts.vehicle': 'Shifts in progress',
  'driver_logs.vehicle': 'Shift log',
  'settings.fleet_retired': 'Retired-fleet register',
  'settings.pending_vehicle_swaps': 'Scheduled swaps',
  'settings.vehicle_contracts': 'Lease contracts',
};

const EMPTY_VEHICLE = {
  rego: '', login: '', make: '', model: '', year: '',
  rego_exp: '', insurance_exp: '', service_due: '',
  // A vehicle joins the fleet before anyone is put in it, so a new row starts
  // spare — pick a driver only once it's actually allocated.
  status: 'available', owner: '',
};

// ── Vehicle Drawer ────────────────────────────────────────────────────────────

function VehicleDrawer({ vehicle, mode, personnel, isAdmin, onClose, onSaved, addNotification }) {
  const [form, setForm] = useState({ ...vehicle });
  const [saving, setSaving] = useState(false);

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  // ── Correcting the plate ────────────────────────────────────────────────────
  // The rego is the vehicle's join key: odometer readings, van checks, fines,
  // claims, services and shifts all reference it as a plain string, so editing it
  // in place would orphan them. The server's renameRego moves them together; we
  // always dry-run first so the blast radius is on screen before anything moves.
  const originalRego = (vehicle.rego || '').toUpperCase();
  const nextRego     = (form.rego || '').trim().toUpperCase();
  const regoChanged  = mode === 'edit' && nextRego !== originalRego;
  const regoValid    = /^[A-Z0-9]{2,10}$/.test(nextRego);
  const [impact, setImpact]     = useState(null);   // dry-run result for the typed plate
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!regoChanged || !regoValid) { setImpact(null); return; }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(FLEET_URL, {
          method: 'POST',
          body: JSON.stringify({ action: 'renameRego', from: originalRego, to: nextRego, dryRun: true }),
        }).then(r => r.json());
        if (!cancelled) setImpact(res);
      } catch (e) {
        if (!cancelled) setImpact({ success: false, error: e.message });
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [nextRego, regoChanged, regoValid, originalRego]);

  const handleSave = async () => {
    if (!form.rego) { addNotification('Rego is required', 'error'); return; }
    if (regoChanged && !regoValid) { addNotification('A plate is 2–10 characters, letters and numbers only', 'error'); return; }
    setSaving(true);
    try {
      // Move the plate FIRST. Everything below writes against the corrected rego,
      // so a failed rename stops here with nothing half-changed.
      if (regoChanged) {
        const ren = await fetch(FLEET_URL, {
          method: 'POST',
          body: JSON.stringify({ action: 'renameRego', from: originalRego, to: nextRego, confirm: true }),
        }).then(r => r.json());
        if (!ren.success) {
          addNotification(ren.error || 'Could not change the plate', 'error');
          setSaving(false);
          return;
        }
        addNotification(
          `${originalRego} → ${nextRego}${ren.total > 1 ? ` · ${ren.total} records moved` : ''}`,
          'success',
        );
      }

      const loginToWrite =
        form.status === 'maintenance' ? 'N/A under repairs' :
        form.status === 'available'   ? '' :
        form.login || '';

      const payload = {
        action: mode === 'add' ? 'addFleet' : 'updateFleet',
        rego:          nextRego,
        login:         loginToWrite,
        make:          form.make,
        model:         form.model,
        year:          form.year,
        rego_exp:      form.rego_exp,
        insurance_exp: form.insurance_exp,
        service_due:   form.service_due,
        owner:         form.owner || '',
      };

      const res  = await fetch(FLEET_URL, { method: 'POST', body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success) {
        onSaved({
          ...form,
          rego:  payload.rego,
          login: form.status === 'leased' ? (form.login || '').toUpperCase() : null,
        }, originalRego, mode);
        addNotification(`${payload.rego} ${mode === 'add' ? 'added' : 'updated'}`, 'success');
        onClose();
      } else {
        addNotification(`Save failed: ${data.error || 'unknown error'}`, 'error');
      }
    } catch (err) {
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const activeDrivers = personnel.filter(p => p.status === 'active');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: 520, background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Fleet</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{mode === 'add' ? 'Add Vehicle' : `Edit — ${form.rego}`}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <SectionTitle n={1} title="Vehicle Identity" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
          <div>
            <label style={labelStyle}>Rego *</label>
            <input className="input" style={{ ...inputStyle, textTransform: 'uppercase' }} value={form.rego}
              onChange={e => set('rego', e.target.value)} disabled={mode === 'edit' && !isAdmin} placeholder="e.g. FHP02E" />
            {mode === 'edit' && !isAdmin && (
              <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 5 }}>
                Only an admin can correct a plate.
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Ownership</label>
            <select className="input" style={inputStyle} value={form.owner || ''} onChange={e => set('owner', e.target.value)}>
              <option value="">— Select owner —</option>
              {OWNER_OPTIONS.filter(o => !['PH Bouveng Pty Ltd','Hollypaul Leasing Pty Ltd'].includes(o.value)).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Assignment</label>
            <select className="input" style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="leased">Leased — allocated to a driver</option>
              <option value="available">Available — spare, not allocated</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Make</label>
            <input className="input" style={inputStyle} value={form.make} onChange={e => set('make', e.target.value)} placeholder="e.g. Toyota" />
          </div>
          <div>
            <label style={labelStyle}>Model</label>
            <input className="input" style={inputStyle} value={form.model} onChange={e => set('model', e.target.value)} placeholder="e.g. HiAce" />
          </div>
          <div>
            <label style={labelStyle}>Year</label>
            <input className="input" style={inputStyle} value={form.year} onChange={e => set('year', e.target.value)} placeholder="e.g. 2023" />
          </div>
        </div>

        {regoChanged && !regoValid && (
          <div style={{ fontSize: '.78rem', color: 'var(--hp-amber)', marginBottom: 14 }}>
            A plate is 2–10 characters, letters and numbers only.
          </div>
        )}

        {checking && (
          <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 14 }}>
            Checking what’s linked to {originalRego}…
          </div>
        )}

        {impact && !impact.success && regoChanged && regoValid && !checking && (
          <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.4)', color: '#ef4444', borderRadius: 8, padding: '9px 12px', marginBottom: 14, fontSize: '.8rem' }}>
            {impact.error || 'Could not check this plate.'}
          </div>
        )}

        {impact && impact.success && regoChanged && regoValid && !checking && (
          <div style={{
            background: impact.total ? 'rgba(245,158,11,.08)' : 'rgba(31,138,76,.1)',
            border: `1px solid ${impact.total ? 'rgba(245,158,11,.35)' : 'rgba(31,138,76,.35)'}`,
            borderRadius: 8, padding: '10px 12px', marginBottom: 18, fontSize: '.8rem',
          }}>
            {impact.total ? (
              <>
                <div style={{ color: 'var(--hp-amber)', fontWeight: 700, marginBottom: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <AlertTriangle size={14} /> {impact.total} linked record{impact.total === 1 ? '' : 's'} move to {nextRego}
                </div>
                <div style={{ maxHeight: 140, overflowY: 'auto' }}>
                  {Object.entries(impact.counts || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', fontSize: '.7rem', padding: '1px 0' }}>
                      <span>{REGO_REF_LABELS[k] || k}</span>
                      <span style={{ color: 'var(--hp-text)', fontWeight: 700 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--hp-green)' }}>
                Clean change — {originalRego} has no linked records yet.
              </div>
            )}
            <div style={{ color: 'var(--hp-dim)', fontSize: '.72rem', marginTop: 8, lineHeight: 1.45 }}>
              Use this to <strong style={{ color: 'var(--hp-text)' }}>correct a plate</strong> — a typo, or the
              same van re-plated. If {form.login || 'the driver'} has moved to a <em>different</em> van, don’t
              rename: add the new vehicle and use <strong style={{ color: 'var(--hp-text)' }}>Vehicle Swaps</strong>,
              which keeps both vans and both histories.
            </div>
          </div>
        )}

        {form.status === 'leased' && (
          <>
            <SectionTitle n={2} title="Assigned Driver" />
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Driver Login</label>
              <select className="input" style={inputStyle} value={form.login || ''} onChange={e => set('login', e.target.value)}>
                <option value="">— Select driver —</option>
                {activeDrivers.map(d => (
                  <option key={d.loginName} value={d.loginName}>{d.loginName} — {d.name}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <SectionTitle n={form.status === 'leased' ? 3 : 2} title="Compliance Dates" />
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 28 }}>
          <div>
            <label style={labelStyle}>Rego Expiry</label>
            <input type="date" className="input" style={inputStyle} value={form.rego_exp} onChange={e => set('rego_exp', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Insurance Expiry</label>
            <input type="date" className="input" style={inputStyle} value={form.insurance_exp} onChange={e => set('insurance_exp', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Service Due</label>
            <input type="date" className="input" style={inputStyle} value={form.service_due} onChange={e => set('service_due', e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSave}
            disabled={saving || (regoChanged && (!regoValid || checking || !!(impact && !impact.success)))}>
            <Save size={14} /> {saving ? 'Saving…' : regoChanged ? `Correct plate & save` : 'Save to Google Sheets'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Retire Modal ──────────────────────────────────────────────────────────────

const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function RetireModal({ vehicle, vehicles, currentUser, onClose, onDone, addNotification }) {
  const editing = !!vehicle.archived; // re-opening a retired vehicle to amend its details
  const [reason, setReason]         = useState(vehicle.archivedReason || '');
  const [replacedBy, setReplacedBy] = useState(vehicle.replacedBy || '');
  const [retiredAt, setRetiredAt]   = useState(vehicle.archivedAt || todayLocalISO());
  const [saving, setSaving]         = useState(false);

  const candidates = vehicles
    .filter(v => !v.archived && v.rego !== vehicle.rego)
    .sort((a, b) => (a.rego > b.rego ? 1 : -1));

  const handleRetire = async () => {
    setSaving(true);
    try {
      const res = await fetch(FLEET_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'setVehicleArchived', rego: vehicle.rego, archived: true,
          reason, replacedBy, archivedAt: retiredAt, by: currentUser?.login || '',
        }),
      }).then(r => r.json());
      if (res.success) {
        addNotification(`${vehicle.rego} ${editing ? 'retirement updated' : 'retired — records kept'}`, 'success');
        onDone(vehicle.rego, { reason, replacedBy: (replacedBy || '').toUpperCase(), retiredAt });
        onClose();
      } else {
        addNotification(`${editing ? 'Update' : 'Retire'} failed: ${res.error || 'unknown error'}`, 'error');
      }
    } catch (e) {
      addNotification(`Error: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ width: 460, background: 'var(--hp-bg)', borderRadius: 14, border: '1px solid var(--hp-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.5)', padding: 26 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.63rem', textTransform: 'uppercase', letterSpacing: '.14em', color: 'var(--hp-gold)', marginBottom: 5 }}>Fleet · {editing ? 'Edit retirement' : 'Retire'}</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{editing ? 'Edit' : 'Retire'} {vehicle.rego}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ margin: '0 0 18px', fontSize: '0.8rem', color: 'var(--hp-muted)' }}>
          {editing
            ? `Update the retirement details for ${vehicle.rego}. Its documents and service history are unaffected.`
            : `Moves ${vehicle.rego} to the Retired tab and off active alerts. All its documents and service history are kept.`}
        </p>
        <label style={labelStyle}>Reason</label>
        <input className="input" style={{ ...inputStyle, width: '100%' }} value={reason}
          onChange={e => setReason(e.target.value)} placeholder="e.g. Traded in, sold, written off" />
        <div style={{ marginTop: 14 }}>
          <label style={labelStyle}>Retirement date</label>
          <input type="date" className="input" style={{ ...inputStyle, width: '100%' }} value={retiredAt}
            onChange={e => setRetiredAt(e.target.value)} />
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={labelStyle}>Replaced by (optional)</label>
          <select className="input" style={{ ...inputStyle, width: '100%' }} value={replacedBy} onChange={e => setReplacedBy(e.target.value)}>
            <option value="">— none —</option>
            {candidates.map(v => (
              <option key={v.rego} value={v.rego}>{v.rego} — {[v.year, v.make, v.model].filter(Boolean).join(' ')}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleRetire} disabled={saving}>
            {editing
              ? <><Save size={14} /> {saving ? 'Saving…' : 'Save changes'}</>
              : <><Archive size={14} /> {saving ? 'Retiring…' : 'Retire vehicle'}</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Fleet Page ────────────────────────────────────────────────────────────────

export default function FleetPage() {
  const navigate = useNavigate();
  const { vehicles, setVehicles, personnel, addNotification, currentUser } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const [activeTab, setActiveTab]         = useState('fleet'); // 'fleet' | 'asset'
  const [filter, setFilter]               = useState('all');
  const [ownerFilter, setOwnerFilter]     = useState('');
  // ?rego= deep link from the global search palette pre-fills the search box;
  // ?driver= does the same from the Onboarding task ("assign a vehicle"), landing on
  // the driver's own row rather than making them search for it.
  const [searchParams] = useSearchParams();
  const regoParam = (searchParams.get('rego') || '').toUpperCase();
  const driverParam = (searchParams.get('driver') || '').toUpperCase();
  const [search, setSearch]               = useState(regoParam || driverParam);
  const [searchBy, setSearchBy]           = useState(regoParam ? 'rego' : driverParam ? 'login' : 'all');
  useEffect(() => { if (regoParam) { setSearch(regoParam); setSearchBy('rego'); } }, [regoParam]);
  useEffect(() => { if (!regoParam && driverParam) { setSearch(driverParam); setSearchBy('login'); } }, [regoParam, driverParam]);
  const [drawer, setDrawer]               = useState(null);
  const [showRegoModal, setShowRegoModal] = useState(false);
  const [showInsModal, setShowInsModal]   = useState(false);  // fleet-wide insurance policy uploader
  const [docsVehicle, setDocsVehicle]     = useState(null);   // vehicle whose private docs drawer is open
  const [scanDocs, setScanDocs]           = useState(false);  // scan-first docs drawer (OCR auto-assigns to a vehicle)
  const [retireTarget, setRetireTarget]   = useState(null);   // vehicle being retired
  const [syncState, setSyncState]         = useState(null);   // null = closed
  const [rowSyncing, setRowSyncing]       = useState(new Set());
  const cancelSync = useRef(false);

  // Retired vehicles stay in the data but drop off the active fleet, counts and alerts.
  const activeVehicles  = useMemo(() => vehicles.filter(v => !v.archived), [vehicles]);
  const retiredVehicles = useMemo(() => vehicles.filter(v => v.archived), [vehicles]);

  const markRetired = (rego, extra) => setVehicles(vs => vs.map(v =>
    v.rego === rego ? { ...v, archived: true, archivedReason: extra.reason, replacedBy: extra.replacedBy, archivedAt: extra.retiredAt } : v));

  const restoreVehicle = async (v) => {
    try {
      const res = await fetch(FLEET_URL, { method: 'POST', body: JSON.stringify({ action: 'setVehicleArchived', rego: v.rego, archived: false }) }).then(r => r.json());
      if (res.success) {
        addNotification(`${v.rego} restored to active fleet`, 'success');
        setVehicles(vs => vs.map(x => x.rego === v.rego ? { ...x, archived: false } : x));
      } else {
        addNotification(`Restore failed: ${res.error || 'unknown error'}`, 'error');
      }
    } catch (e) {
      addNotification(`Error: ${e.message}`, 'error');
    }
  };

  // ── Sync helpers ─────────────────────────────────────────────────────────────

  const saveVehicleToSheet = (v) =>
    fetch(FLEET_URL, {
      method: 'POST',
      body: JSON.stringify({
        action:        'updateFleet',
        rego:          v.rego,
        login:         v.login         || '',
        make:          v.make          || '',
        model:         v.model         || '',
        year:          v.year          || '',
        rego_exp:      v.rego_exp      || '',
        insurance_exp: v.insurance_exp || '',
        service_due:   v.service_due   || '',
        owner:         v.owner         || '',
      }),
    }).catch(() => {});

  const applyApiData = (existing, api) => ({
    ...existing,
    rego_exp: api.rego_exp || existing.rego_exp,
    make:     api.make  || existing.make,
    model:    api.model || existing.model,
    year:     api.year  || existing.year,
  });

  const syncVehicle = async (rego) => {
    setRowSyncing(prev => new Set([...prev, rego]));
    try {
      const res  = await fetch(REGO_CHECK_URL, { method: 'POST', body: JSON.stringify({ plate: rego }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'API error');
      if (!data.found)   { addNotification(`${rego} — not found in registry`, 'error'); return; }
      let updated;
      setVehicles(vs => {
        updated = applyApiData(vs.find(v => v.rego === rego) || { rego }, data);
        return vs.map(v => v.rego === rego ? updated : v);
      });
      // slight delay so state has settled before we read updated
      await new Promise(r => setTimeout(r, 50));
      setVehicles(vs => {
        const v = vs.find(v => v.rego === rego);
        if (v) saveVehicleToSheet(v);
        return vs;
      });
      const details = [data.year, data.make, data.model].filter(Boolean).join(' ');
      addNotification(`${rego} — vehicle details synced${details ? ': ' + details : ''}`, 'success');
    } catch (err) {
      addNotification(`${rego} sync error: ${err.message}`, 'error');
    } finally {
      setRowSyncing(prev => { const n = new Set(prev); n.delete(rego); return n; });
    }
  };

  const syncAllRego = async () => {
    const toSync = activeVehicles.filter(v => v.rego);
    cancelSync.current = false;
    setSyncState({ running: true, total: toSync.length, done: 0, results: [], currentRego: toSync[0]?.rego || '' });

    for (let i = 0; i < toSync.length; i++) {
      if (cancelSync.current) break;
      const v = toSync[i];
      setSyncState(prev => ({ ...prev, currentRego: v.rego }));
      try {
        const res  = await fetch(REGO_CHECK_URL, { method: 'POST', body: JSON.stringify({ plate: v.rego }) });
        const data = await res.json();
        if (data.success && data.found) {
          let updated;
          setVehicles(vs => {
            updated = applyApiData(vs.find(cv => cv.rego === v.rego) || v, data);
            return vs.map(cv => cv.rego === v.rego ? updated : cv);
          });
          if (updated) saveVehicleToSheet(updated);
          setSyncState(prev => ({ ...prev, done: i + 1, results: [...prev.results, { rego: v.rego, status: 'updated', make: data.make, model: data.model, year: data.year }] }));
        } else {
          setSyncState(prev => ({ ...prev, done: i + 1, results: [...prev.results, { rego: v.rego, status: data.found === false ? 'not_found' : 'error', error: data.error }] }));
        }
      } catch (err) {
        setSyncState(prev => ({ ...prev, done: i + 1, results: [...prev.results, { rego: v.rego, status: 'error', error: err.message }] }));
      }
      if (i < toSync.length - 1) await new Promise(r => setTimeout(r, 400));
    }
    setSyncState(prev => ({ ...prev, running: false, currentRego: '' }));
  };

  const filtered = activeVehicles.filter(v => {
    const statusMatch = filter === 'all' || v.status === filter;
    const ownerOpt    = OWNER_OPTIONS.find(o => o.value === ownerFilter);
    const ownerMatch  = !ownerFilter || v.owner === ownerFilter ||
      (ownerOpt && OWNER_OPTIONS.find(o => o.short === ownerOpt.short && o.value === v.owner));
    if (!search.trim()) return statusMatch && ownerMatch;
    const q         = search.trim().toUpperCase();
    const regoMatch  = (v.rego  || '').toUpperCase().includes(q);
    const loginMatch = (v.login || '').toUpperCase().includes(q);
    const nameMatch  = !!personnel.find(p => p.loginName?.toUpperCase() === v.login?.toUpperCase())?.name?.toUpperCase().includes(q);
    const fieldMatch =
      searchBy === 'rego'  ? regoMatch :
      searchBy === 'login' ? (loginMatch || nameMatch) :
      (regoMatch || loginMatch || nameMatch);
    return statusMatch && ownerMatch && fieldMatch;
  });

  const getDriver = login =>
    login ? personnel.find(p => p.loginName?.toUpperCase() === login.toUpperCase()) : null;

  const alertCount = activeVehicles.filter(v => {
    const regoD = daysUntil(v.rego_exp);
    const insD  = daysUntil(v.insurance_exp);
    return (regoD !== null && regoD < 60) || (insD !== null && insD < 30) || v.status === 'maintenance';
  }).length;

  // `prevRego` is the plate the row was under before a correction — match on that
  // so a renamed vehicle replaces its own row instead of appearing twice.
  const handleSaved = (updated, prevRego, mode) => {
    const key = prevRego || updated.rego;
    setVehicles(vs => {
      const exists = vs.find(v => v.rego === key);
      if (exists) return vs.map(v => v.rego === key ? { ...v, ...updated } : v);
      return [...vs, { ...updated, id: `v${String(vs.length + 1).padStart(3, '0')}` }];
    });
    // A new vehicle arrives with its paperwork in hand — hand straight to Documents
    // so the contract of sale and tax invoice are filed while they're in front of you,
    // instead of the row being saved and the file never started.
    if (mode === 'add' && isAdmin) setDocsVehicle({ ...updated, login: updated.login || '' });
  };

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Fleet</h2>
          <p>Hollypaul Leasing Pty Ltd — {activeVehicles.length} commercial vehicles</p>
          {/* ── Tab switcher ── */}
          <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
            {[['fleet', 'Operations'], ...(isAdmin ? [['swaps', 'Vehicle Swaps']] : []), ['asset', 'Asset Value'], ['retired', `Retired${retiredVehicles.length ? ` (${retiredVehicles.length})` : ''}`]].map(([key, label]) => (
              <button
                key={key}
                className={`btn btn-sm ${activeTab === key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {activeTab === 'fleet' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => setShowRegoModal(true)}>
              <ExternalLink size={14} /> Check All Rego
            </button>
            {isAdmin && (
              <button className="btn btn-secondary" onClick={() => setScanDocs(true)} title="Scan a document — OCR reads the rego and files it against the matching vehicle (admin only)">
                <Camera size={14} /> Scan Document
              </button>
            )}
            {isAdmin && (
              <button className="btn btn-secondary" onClick={() => setShowInsModal(true)}>
                <ShieldCheck size={14} /> Insurance Policy
              </button>
            )}
            <button className="btn btn-secondary" onClick={syncAllRego} disabled={syncState?.running}>
              <RefreshCw size={14} /> Sync Rego Dates
            </button>
            <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'add', vehicle: { ...EMPTY_VEHICLE } })}>
              <Plus size={14} /> Add Vehicle
            </button>
          </div>
        )}
      </div>

      <div className="page-body">

        {/* ── Asset Value tab ─────────────────────────────────────────────── */}
        {activeTab === 'asset' && <AssetValueContent />}

        {/* ── Vehicle Swaps tab (admin only) ───────────────────────────────── */}
        {activeTab === 'swaps' && isAdmin && (
          <FleetSwapsTab
            vehicles={activeVehicles}
            personnel={personnel}
            currentUser={currentUser}
            addNotification={addNotification}
            onPermanentSwap={(rego, login, released = []) => setVehicles(vs => vs.map(v =>
              v.rego === rego ? { ...v, login }
                : released.includes(v.rego) ? { ...v, login: '' }
                  : v))}
            onFleetLogins={(map) => setVehicles(vs => vs.map(v =>
              Object.prototype.hasOwnProperty.call(map, v.rego) ? { ...v, login: map[v.rego] } : v))}
          />
        )}

        {/* ── Fleet operations tab ─────────────────────────────────────────── */}
        {activeTab === 'fleet' && <>
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: '24px' }}>
          {[
            { label: 'Total Fleet',       value: activeVehicles.length,                                       accent: 'var(--hp-gold)' },
            { label: 'Currently Leased',  value: activeVehicles.filter(v => v.status === 'leased').length,    accent: 'var(--hp-green)' },
            { label: 'Available',         value: activeVehicles.filter(v => v.status === 'available').length, accent: 'var(--hp-blue)' },
            { label: 'Compliance Alerts', value: alertCount, accent: alertCount > 0 ? 'var(--hp-red)' : 'var(--hp-green)' },
          ].map(s => (
            <div className="stat-card" key={s.label} style={{ '--accent': s.accent }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ fontSize: '2rem' }}>{s.value}</div>
            </div>
          ))}
        </div>

        <AlertPanel vehicles={activeVehicles} addNotification={addNotification} />

        {/* Search / filter bar */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '0 0 280px' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-muted)', pointerEvents: 'none' }} />
            <input
              className="input"
              style={{ paddingLeft: 32, background: 'var(--hp-surface)', color: 'var(--hp-text)', width: '100%' }}
              placeholder="Search fleet…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[['all','All'],['rego','Rego'],['login','Driver']].map(([val, label]) => (
              <button key={val} className={`btn btn-sm ${searchBy === val ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setSearchBy(val)}>{label}</button>
            ))}
          </div>
          <div style={{ width: '1px', height: '24px', background: 'var(--hp-border)', margin: '0 4px' }} />
          <select
            className="input"
            style={{ background: 'var(--hp-surface)', color: ownerFilter ? 'var(--hp-text)' : 'var(--hp-muted)', fontSize: '0.8rem', padding: '0 10px', height: '34px', minWidth: '180px' }}
            value={ownerFilter}
            onChange={e => setOwnerFilter(e.target.value)}
          >
            <option value="">All Owners</option>
            {OWNER_OPTIONS.filter(o => !['PH Bouveng Pty Ltd','Hollypaul Leasing Pty Ltd'].includes(o.value)).map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {search.trim() && (
            <span style={{ fontSize: '0.75rem', color: 'var(--hp-muted)', marginLeft: 'auto' }}>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Registration</th>
                <th>Ownership</th>
                <th>Leased To</th>
                <th>Rego Expiry</th>
                <th>Insurance Exp</th>
                <th>Next Service</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => {
                const driver = getDriver(v.login);
                return (
                  <tr key={v.rego}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '32px', height: '32px', background: 'var(--hp-surface)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Truck size={14} color="var(--hp-dim)" />
                        </div>
                        <div style={{ fontWeight: 500 }}>
                          {v.year || v.make || v.model
                            ? `${v.year} ${v.make} ${v.model}`.trim()
                            : <span style={{ color: 'var(--hp-muted)', fontStyle: 'italic' }}>Details pending</span>
                          }
                        </div>
                      </div>
                    </td>
                    <td><span className="font-mono" style={{ fontSize: '0.85rem', color: 'var(--hp-gold)' }}>{v.rego}</span></td>
                    <td><OwnerBadge owner={v.owner} /></td>
                    <td>
                      {driver
                        ? <div>
                            <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{driver.name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>{driver.loginName}</div>
                          </div>
                        : v.login
                          ? <span style={{ fontSize: '0.8rem', color: 'var(--hp-muted)' }}>{v.login}</span>
                          : <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>—</span>
                      }
                    </td>
                    <td><CompliancePill label="rego" date={v.rego_exp} verifiedAt={v.regoCheckedAt} /></td>
                    <td><CompliancePill label="insurance" date={v.insurance_exp} certifiedAt={v.insuranceDocAt} /></td>
                    <td><CompliancePill label="service" date={v.service_due} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          title={v.regoCheckedAt
                            ? `Sync rego date from API — last verified ${new Date(v.regoCheckedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}`
                            : 'Sync rego date from API'}
                          disabled={rowSyncing.has(v.rego)}
                          onClick={() => syncVehicle(v.rego)}
                        >
                          {rowSyncing.has(v.rego)
                            ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                            : <RefreshCw size={12} />
                          }
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          title="Check on Service NSW"
                          onClick={() => checkRego(v.rego, addNotification)}
                        >
                          <ExternalLink size={12} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          title="Service & repair history"
                          onClick={() => navigate(`/vehicle-service?rego=${encodeURIComponent(v.rego)}`)}
                        >
                          <Wrench size={12} /> Service
                        </button>
                        {isAdmin && (
                          <button
                            className="btn btn-ghost btn-sm"
                            title="Private documents — rego, purchase, insurance (admin only)"
                            onClick={() => setDocsVehicle(v)}
                          >
                            <FileText size={12} /> Docs
                          </button>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setDrawer({ mode: 'edit', vehicle: { ...v, login: v.login || '' } })}
                        >
                          Edit
                        </button>
                        {isAdmin && (
                          <button
                            className="btn btn-ghost btn-sm"
                            title="Retire / dispose — keeps all records"
                            onClick={() => setRetireTarget(v)}
                          >
                            <Archive size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
          <span>Colour guide:</span>
          {[
            { color: 'var(--hp-red)',   label: 'Expired / Overdue' },
            { color: 'var(--hp-amber)', label: '< 30 days' },
            { color: 'var(--hp-gold)',  label: '30–60 days' },
          ].map(({ color, label }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, display: 'inline-block' }} />
              {label}
            </span>
          ))}
        </div>
        </>}

        {/* ── Retired assets tab ───────────────────────────────────────────── */}
        {activeTab === 'retired' && (
          retiredVehicles.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--hp-muted)', fontSize: '0.9rem', border: '1px dashed var(--hp-border)', borderRadius: 10 }}>
              <Archive size={22} style={{ opacity: 0.5, marginBottom: 8 }} />
              <div>No retired vehicles. Retire one from the Operations tab (the <Archive size={12} style={{ verticalAlign: -1 }} /> button) — it keeps all its documents and service history.</div>
            </div>
          ) : (
            <div className="card">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Registration</th>
                    <th>Ownership</th>
                    <th>Retired</th>
                    <th>Reason</th>
                    <th>Replaced By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {retiredVehicles.map(v => (
                    <tr key={v.rego} style={{ opacity: 0.85 }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '32px', height: '32px', background: 'var(--hp-surface)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Archive size={14} color="var(--hp-dim)" />
                          </div>
                          <div style={{ fontWeight: 500 }}>
                            {v.year || v.make || v.model
                              ? `${v.year} ${v.make} ${v.model}`.trim()
                              : <span style={{ color: 'var(--hp-muted)', fontStyle: 'italic' }}>Details pending</span>}
                          </div>
                        </div>
                      </td>
                      <td><span className="font-mono" style={{ fontSize: '0.85rem', color: 'var(--hp-gold)' }}>{v.rego}</span></td>
                      <td><OwnerBadge owner={v.owner} /></td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>
                        {v.archivedAt
                          ? new Date(v.archivedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
                          : <span style={{ color: 'var(--hp-muted)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>{v.archivedReason || <span style={{ color: 'var(--hp-muted)' }}>—</span>}</td>
                      <td>
                        {v.replacedBy
                          ? <span className="font-mono" style={{ fontSize: '0.82rem', color: 'var(--hp-text)' }}>{v.replacedBy}</span>
                          : <span style={{ color: 'var(--hp-muted)', fontSize: '0.8rem' }}>—</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            title="Service & repair history"
                            onClick={() => navigate(`/vehicle-service?rego=${encodeURIComponent(v.rego)}`)}
                          >
                            <Wrench size={12} /> Service
                          </button>
                          {isAdmin && (
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Private documents (admin only)"
                              onClick={() => setDocsVehicle(v)}
                            >
                              <FileText size={12} /> Docs
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Edit retirement details (date, reason, replacement)"
                              onClick={() => setRetireTarget(v)}
                            >
                              <Save size={12} /> Edit
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Restore to active fleet"
                              onClick={() => restoreVehicle(v)}
                            >
                              <RotateCcw size={12} /> Restore
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {drawer && (
        <VehicleDrawer
          vehicle={drawer.vehicle} mode={drawer.mode} personnel={personnel}
          isAdmin={isAdmin}
          onClose={() => setDrawer(null)} onSaved={handleSaved} addNotification={addNotification}
        />
      )}

      {showRegoModal && (
        <RegoCheckModal
          vehicles={activeVehicles} personnel={personnel}
          onClose={() => setShowRegoModal(false)} addNotification={addNotification}
        />
      )}

      {docsVehicle && (
        <VehicleDocsDrawer
          vehicle={docsVehicle} currentUser={currentUser}
          onClose={() => setDocsVehicle(null)} addNotification={addNotification}
        />
      )}

      {scanDocs && (
        <VehicleDocsDrawer
          vehicles={activeVehicles} currentUser={currentUser}
          onClose={() => setScanDocs(false)} addNotification={addNotification}
        />
      )}

      {showInsModal && (
        <FleetInsuranceModal
          vehicles={vehicles} setVehicles={setVehicles} currentUser={currentUser}
          onClose={() => setShowInsModal(false)} addNotification={addNotification}
        />
      )}

      {retireTarget && (
        <RetireModal
          vehicle={retireTarget} vehicles={vehicles} currentUser={currentUser}
          onClose={() => setRetireTarget(null)} onDone={markRetired} addNotification={addNotification}
        />
      )}

      {syncState && (
        <SyncProgressModal
          state={syncState}
          onClose={() => setSyncState(null)}
        />
      )}
    </>
  );
}
